<template>
  <div
    ref="stageEl"
    class="fm"
    :class="[`fm-tier-${tier}`, { 'fm-dark': dark, 'is-run': running || armed, 'is-armed': armed }]"
    :style="{ '--cycle': `${cycle}ms`, '--fm-h': `${height}px`, '--fm-h-sm': `${heightSm || height}px` }"
  >
    <div class="fm-clip" aria-hidden="true">
      <div :key="runKey" class="fm-window">
        <div class="fm-titlebar">
          <svg viewBox="0 0 24 24" class="fm-titlebar-icon" aria-hidden="true"><path :d="icon" /></svg>
          <span>{{ title }}</span>
        </div>
        <div class="fm-body">
          <slot />
        </div>
      </div>
    </div>
    <!-- Server-rendered (hidden) so its label reaches i18n:extract and the localized editions. -->
    <button v-show="canReplay" type="button" class="fm-replay" :aria-label="replayLabel" :title="replayLabel" @click="replay">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" /></svg>
    </button>
  </div>
</template>

<script setup>
// The app's feature preview stage (Workspace369 lib/view/subscription_packages/unlock/
// unlock_preview_stage.dart), for the site: a plan-tinted panel with a fading dot grid holding a
// small app window in which a scene plays. The scene plays `plays` times, then rests on its last
// frame (the payoff) with a Replay button, so nothing moves for more than about ten seconds
// unasked (WCAG 2.2.2): at the default 5 s cycle the last beat of play 2 lands at about 8.5 s.
// With reduce-motion on, or without JS, the last frame shows; once mounted, the stage holds the
// first frame (armed) until it scrolls into view, so the first play never snaps back from the end. Beats are CSS animations on opacity and transform only: every beat's resting style IS
// the final frame, and `.is-run` plays the beats from their start values (see the kit below).
const props = defineProps({
  title: { type: String, required: true },
  // A 24x24 Material icon path for the window's title bar.
  icon: { type: String, required: true },
  // The plan tier the stage is tinted with: 1 Cadet … 5 Enterprise.
  tier: { type: Number, default: 1 },
  // The app plays 5.6 s; the site uses 5 s so two plays stay well inside ten seconds.
  cycle: { type: Number, default: 5000 },
  plays: { type: Number, default: 2 },
  // Reserved stage height (no layout shift); heightSm applies below 640px.
  height: { type: Number, default: 340 },
  heightSm: { type: Number, default: 0 },
  // Dark sections (the integration hero) always draw the app's dark theme.
  dark: { type: Boolean, default: false },
  replayLabel: { type: String, default: 'Replay preview' },
});

const stageEl = ref(null);
const running = ref(false);
const armed = ref(false);
const resting = ref(false);
const runKey = ref(0);
const reduced = ref(false);
let played = 0;
let timer = null;
let observer = null;

const canReplay = computed(() => resting.value && !reduced.value);

const play = () => {
  clearTimeout(timer);
  resting.value = false;
  armed.value = false;
  running.value = true;
  // A new key remounts the window, which restarts every beat's animation from its start.
  runKey.value += 1;
  timer = setTimeout(() => {
    played += 1;
    if (played < props.plays) play();
    else {
      running.value = false;
      resting.value = true;
    }
  }, props.cycle);
};

const replay = () => {
  played = props.plays - 1;
  play();
};

onMounted(() => {
  reduced.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced.value) return;
  armed.value = true;
  observer = new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting) return;
    observer.disconnect();
    played = 0;
    play();
  }, { threshold: 0.35 });
  observer.observe(stageEl.value);
});

onBeforeUnmount(() => {
  clearTimeout(timer);
  observer?.disconnect();
});
</script>

<style>
/* ─── Stage ───────────────────────────────────────────────────────────────
   The palette also applies to `.fm-tokens`, for motion drawn outside a stage (the MCP page's
   setup timeline, use-case outcomes, app lanes and Send without asking switch). */
.fm,
.fm-tokens {
  --fm-surface: #fff;
  --fm-sunken: #f7f7f7;
  --fm-line: rgb(0 0 0 / 0.12);
  --fm-text: #333;
  --fm-muted: #595f68;
  --fm-faint: #8a8f96;
  --fm-ink: #171717;
  --fm-ink-text: #fff;
  --fm-dot: rgb(0 0 0 / 0.12);
  --fm-wash: rgb(0 0 0 / 0.018);
  --fm-glow-a: 0.14;
  --fm-shadow: 0 6px 22px rgb(0 0 0 / 0.07);
  /* App tones (lib/view/widgets/core/app_tone.dart), light. */
  --fm-green: #2d7a4e;
  --fm-green-fill: #e6f4ea;
  --fm-amber: #946000;
  --fm-amber-fill: #fdf4c3;
  --fm-blue: #3f58bf;
  --fm-blue-fill: #ebf0fd;
  --fm-red: #ba3a37;
  --fm-red-fill: #fbeaea;
  --fm-orange: #ad4e00;
  --fm-orange-fill: #fcedd6;
  --fm-purple: #7948af;
  --fm-purple-fill: #f4ecfc;
  --fm-gray: #5e5e5e;
  --fm-gray-fill: rgb(0 0 0 / 0.067);
}
.fm {
  position: relative;
  height: var(--fm-h);
  overflow: hidden;
  /* A stage's height is fixed, so the browser can skip drawing one that is off screen without
     anything moving when it scrolls in (the scene only starts once it is in view anyway). */
  content-visibility: auto;
  contain-intrinsic-size: auto var(--fm-h);
  border-radius: 14px;
  background-color: var(--fm-wash);
  color: var(--fm-text);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  isolation: isolate;
}
.fm-dark,
.dark .fm,
.dark .fm-tokens {
  --fm-surface: #242424;
  --fm-sunken: #1a1a1a;
  --fm-line: rgb(255 255 255 / 0.15);
  --fm-text: #e5e5e5;
  --fm-muted: #a3a3a3;
  --fm-faint: #8a8a8a;
  --fm-ink: #e5e5e5;
  --fm-ink-text: #171717;
  --fm-dot: rgb(255 255 255 / 0.15);
  --fm-wash: rgb(255 255 255 / 0.03);
  --fm-glow-a: 0.22;
  --fm-shadow: 0 6px 22px rgb(0 0 0 / 0.32);
  --fm-green: #6ed39a;
  --fm-green-fill: rgb(63 178 110 / 0.18);
  --fm-amber: #f1c95e;
  --fm-amber-fill: rgb(224 170 36 / 0.17);
  --fm-blue: #93aef9;
  --fm-blue-fill: rgb(91 127 234 / 0.2);
  --fm-red: #f69591;
  --fm-red-fill: rgb(229 83 78 / 0.2);
  --fm-orange: #f6ac6b;
  --fm-orange-fill: rgb(233 131 45 / 0.18);
  --fm-purple: #bea5f2;
  --fm-purple-fill: rgb(154 111 224 / 0.2);
  --fm-gray: #bdbdbd;
  --fm-gray-fill: rgb(255 255 255 / 0.12);
}
/* Plan palettes (PlanCardPalettes) and the payoff tone (UnlockPlans.toneFor). */
.fm-tier-1,
.fm-tokens { --fm-p1: 124 155 224; --fm-p2: 159 139 230; --fm-tone: var(--fm-blue); }
.fm-tier-2 { --fm-p1: 224 138 176; --fm-p2: 197 138 227; --fm-tone: #b4336b; }
.fm-tier-3 { --fm-p1: 233 107 76; --fm-p2: 201 61 92; --fm-tone: #ad4e00; }
.fm-tier-4 { --fm-p1: 224 138 92; --fm-p2: 206 111 140; --fm-tone: #946000; }
.fm-tier-5 { --fm-p1: 140 155 184; --fm-p2: 108 122 160; --fm-tone: #7948af; }
.fm-dark.fm-tier-2, .dark .fm-tier-2 { --fm-tone: #f392bd; }
.fm-dark.fm-tier-3, .dark .fm-tier-3 { --fm-tone: #f6ac6b; }
.fm-dark.fm-tier-4, .dark .fm-tier-4 { --fm-tone: #f1c95e; }
.fm-dark.fm-tier-5, .dark .fm-tier-5 { --fm-tone: #bea5f2; }
.fm::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    radial-gradient(circle, var(--fm-dot) 0.8px, transparent 1.3px) 7px 7px / 14px 14px,
    radial-gradient(150% 150% at 50% 45%, rgb(var(--fm-p1) / var(--fm-glow-a)) 0%, rgb(var(--fm-p2) / calc(var(--fm-glow-a) * 0.55)) 50%, rgb(var(--fm-p2) / 0) 100%);
  -webkit-mask-image: radial-gradient(closest-side, #000 70%, rgb(0 0 0 / 0.55) 100%);
  mask-image: radial-gradient(closest-side, #000 70%, rgb(0 0 0 / 0.55) 100%);
}
/* The window may run past the stage's bottom: it dissolves into the backdrop. */
.fm-clip {
  height: 100%;
  padding: 22px 28px 0;
  -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 40px), transparent);
  mask-image: linear-gradient(to bottom, #000 calc(100% - 40px), transparent);
}
.fm-window {
  max-width: 400px;
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid var(--fm-line);
  border-radius: 10px;
  background: var(--fm-surface);
  box-shadow: var(--fm-shadow);
  font-size: 12px;
  line-height: 1.4;
}
.fm-titlebar {
  display: flex;
  align-items: center;
  gap: 6px;
  border-bottom: 1px solid var(--fm-line);
  padding: 9px 12px;
  color: var(--fm-muted);
  font-weight: 500;
}
.fm-titlebar-icon { width: 14px; height: 14px; flex: none; fill: currentColor; }
.fm-body { padding: 12px 14px 14px; }
.fm-replay {
  position: absolute;
  right: 10px;
  bottom: 4px;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--fm-muted);
  cursor: pointer;
}
.fm-replay:hover { background: var(--fm-line); color: var(--fm-text); }
.fm-replay:active { background: color-mix(in srgb, var(--fm-tone) 22%, transparent); color: var(--fm-text); transform: scale(0.94); }
.fm-replay:focus-visible { outline: 2px solid var(--fm-tone); outline-offset: 1px; }
.fm-replay svg { width: 20px; height: 20px; fill: currentColor; }
@media (max-width: 639px) {
  .fm { height: var(--fm-h-sm); contain-intrinsic-size: auto var(--fm-h-sm); }
  /* Phones: the window takes the full width, so the bottom 44px is kept for Replay. */
  .fm-clip { height: calc(100% - 44px); padding: 16px 14px 0; }
}

/* ─── Scene kit ─────────────────────────────────────────────────────────────
   Times are fractions of the cycle, set per element: --at (start) and --len.
   Resting styles are the final frame; `.is-run` animates from the start values. */
.fm-stack { display: grid; }
.fm-stack > * { grid-area: 1 / 1; }
/* Present at the start, gone by the end (a beat swaps it out). */
.fm-out { opacity: 0; visibility: hidden; }
/* The pointer and its ripple only exist while playing. */
.fm-pointer, .fm-ripple { opacity: 0; }

:is(.fm, .fm-tokens).is-run .fm-window {
  animation: fm-settle calc(var(--cycle) * 0.1) cubic-bezier(0.33, 1, 0.68, 1) both;
}
:is(.fm, .fm-tokens).is-run .fm-rise {
  animation: fm-rise calc(var(--cycle) * var(--len, 0.12)) cubic-bezier(0.33, 1, 0.68, 1) calc(var(--cycle) * var(--at, 0)) both;
}
:is(.fm, .fm-tokens).is-run .fm-in {
  animation: fm-fade calc(var(--cycle) * var(--len, 0.08)) ease-out calc(var(--cycle) * var(--at, 0)) both;
}
:is(.fm, .fm-tokens).is-run .fm-out {
  animation: fm-gone calc(var(--cycle) * var(--len, 0.06)) ease-in calc(var(--cycle) * var(--at, 0)) both;
}
/* Armed: every beat holds its start value (the first frame) until the stage is in view. */
:is(.fm, .fm-tokens).is-armed *, :is(.fm, .fm-tokens).is-armed *::before, :is(.fm, .fm-tokens).is-armed *::after { animation-play-state: paused !important; }
@keyframes fm-settle { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: none; } }
@keyframes fm-rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes fm-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes fm-gone { from { opacity: 1; visibility: visible; } to { opacity: 0; visibility: hidden; } }

/* The "look here" cue (UnlockFocus): a soft 1.5px ring in the plan's tone that pulses once at
   --at, then rests, so the last frame still points at the payoff. */
.fm-focus { position: relative; }
.fm-focus::before,
.fm-focus::after {
  content: "";
  position: absolute;
  inset: calc(var(--grow, 4px) * -1);
  border-radius: var(--radius, 9px);
  pointer-events: none;
}
.fm-focus::before {
  border: 1.5px solid color-mix(in srgb, var(--fm-tone) 30%, transparent);
  background: color-mix(in srgb, var(--fm-tone) 5%, transparent);
}
.fm-focus::after {
  border: 1.5px solid color-mix(in srgb, var(--fm-tone) 88%, transparent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--fm-tone) 20%, transparent);
  opacity: 0;
}
:is(.fm, .fm-tokens).is-run .fm-focus::before {
  animation: fm-fade calc(var(--cycle) * 0.05) ease-out calc(var(--cycle) * var(--at, 0)) both;
}
:is(.fm, .fm-tokens).is-run .fm-focus::after {
  animation: fm-pulse calc(var(--cycle) * 0.2) linear calc(var(--cycle) * var(--at, 0)) both;
}
@keyframes fm-pulse { 0% { opacity: 0; } 50% { opacity: 1; } 100% { opacity: 0; } }

/* The pointer (UnlockPointer): its top-left is the click point. It glides in over 0.14 of the
   cycle, dips as it presses at --press (a ring pulses out in the plan's tone) and fades. */
.fm-pointer {
  position: absolute;
  z-index: 3;
  width: 14px;
  height: 20px;
  pointer-events: none;
  transform-origin: 0 0;
}
.fm-pointer svg { display: block; width: 14px; height: 20px; overflow: visible; filter: drop-shadow(0 1.5px 2px rgb(0 0 0 / 0.22)); }
.fm-pointer path { fill: var(--fm-ink); stroke: var(--fm-surface); stroke-width: 2.4px; stroke-linejoin: round; paint-order: stroke; }
.fm-ripple {
  position: absolute;
  z-index: 2;
  width: 40px;
  height: 40px;
  margin: -20px 0 0 -20px;
  border: 1.5px solid var(--fm-tone);
  border-radius: 999px;
  pointer-events: none;
}
:is(.fm, .fm-tokens).is-run .fm-pointer {
  animation: fm-pointer calc(var(--cycle) * 0.41) linear calc(var(--cycle) * (var(--press) - 0.19)) both;
}
:is(.fm, .fm-tokens).is-run .fm-ripple {
  animation: fm-ripple calc(var(--cycle) * 0.14) ease-out calc(var(--cycle) * var(--press)) both;
}
@keyframes fm-pointer {
  0% { opacity: 0; transform: translate(30px, 22px); animation-timing-function: cubic-bezier(0.33, 1, 0.68, 1); }
  34% { opacity: 1; transform: none; }
  39% { transform: none; }
  46% { transform: scale(0.84); }
  51% { transform: scale(0.84); }
  63% { opacity: 1; transform: none; }
  71% { opacity: 1; transform: none; }
  100% { opacity: 0; transform: none; }
}
/* 0% stays invisible: with fill "both", the first keyframe shows during the delay. */
@keyframes fm-ripple {
  0% { opacity: 0; transform: scale(0.2); }
  1% { opacity: 0.6; transform: scale(0.2); }
  100% { opacity: 0; transform: scale(1); }
}

@media (prefers-reduced-motion: reduce) {
  :is(.fm, .fm-tokens).is-run *, :is(.fm, .fm-tokens).is-run *::before, :is(.fm, .fm-tokens).is-run *::after { animation: none !important; }
}
</style>
